import {
  AlignmentType, Document, HeadingLevel, ImageRun, Packer, Paragraph, Table, TableCell, TableRow,
} from 'docx';
import PptxGenJS from 'pptxgenjs';
import * as XLSX from 'xlsx';
import { ReportDocument, TableData } from '../../../../agent/runtime/files';
import {
  cellText, chartSvg, exceptionCaption, figureCaption, notedItems, NOTICE_TO, noticeItems, noticeSign, noticeTables, reportFigures,
  reportHtml, reportMeta, reportNotes, sectionTitles, seriesColor, STATUS_CAPTION, tablesAt,
} from '../../../../agent/runtime/reportHtml';
import JSZip from 'jszip';
import { keynoteDeck, plainCategoryXml, reportSlides } from '../../../../agent/runtime/slides';

const FORBIDDEN = /[\\/:*?"<>|]/g;

export function fileName(question: string, extension: string): string {
  const stem = question.slice(0, 20).replace(FORBIDDEN, '_').trim() || 'AI';
  const today = new Date();
  const stamp = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('');
  return `${stem}_${stamp}.${extension}`;
}

function save(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function truncationNote(table: TableData): string | null {
  return table.truncated ? `${table.truncated}행이 용량 제한으로 잘렸습니다.` : null;
}

function reportRows(report: ReportDocument): unknown[][] {
  return [
    [report.title],
    ['대상 기간', report.period],
    ...(report.audience ? [['보고 대상', report.audience]] : []),
    ...(report.cadence ? [['보고 주기', report.cadence]] : []),
    [],
    ['핵심 지표'],
    ['지표', '값'],
    ...report.kpis.map((kpi) => [kpi.label, kpi.value]),
    [],
    [report.summaryHeading ?? '현황 요약'],
    [report.summary],
    [],
    ['산출 기준', report.methodology],
    ...(report.sources.length ? [['조회 화면', report.sources.join(', ')]] : []),
  ];
}

export function toExcel(table: TableData, question: string, report?: ReportDocument): void {
  const note = truncationNote(table);
  const detail = report?.table ?? table;
  const sheet = XLSX.utils.aoa_to_sheet([
    ...(note ? [[note]] : []),
    detail.columns,
    ...detail.rows,
  ]);
  const book = XLSX.utils.book_new();
  if (report) {
    const summary = XLSX.utils.aoa_to_sheet(reportRows(report));
    summary['!cols'] = [{ wch: 24 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(book, summary, '보고요약');
  }
  XLSX.utils.book_append_sheet(book, sheet, report ? '상세내역' : '결과');
  const bytes = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  save(
    new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    fileName(question, 'xlsx'),
  );
}

/** 문서에 넣을 만큼의 표. 엑셀은 늘 전체다 — 줄였으면 그 사실을 적는다. */
function documentTable(report: ReportDocument | undefined, table: TableData | undefined): {
  table?: TableData; note?: string;
} {
  // 원자료는 0 이면 싣지 않고, 아니면 전부. 「앞 N건만 실었습니다」 같은 화면 안내는 보고 문서에 넣지 않는다.
  return report?.detailRows === 0 ? {} : { table };
}

function wordTable(table: TableData): Table {
  // 문서와 PDF 의 표는 사람이 읽는다. 금액 열이 `455666061` 로 찍히면 자릿수를 세어야
  // 한다. 엑셀은 손대지 않는다 — 숫자로 넣어야 시트에서 계산과 서식이 산다.
  return new Table({
    rows: [table.columns, ...table.rows.map((row) => row.map((cell) => cellText(cell)))]
      .map((row) => new TableRow({
        children: row.map((cell) => new TableCell({ children: [new Paragraph(String(cell))] })),
      })),
  });
}

/** 워드 본문 폭에 맞춘 그림 폭(px). A4 여백 안에 들어간다. */
const WORD_CHART_WIDTH = 600;

/**
 * SVG 를 PNG 로. 한글·LibreOffice·예전 워드는 SVG 를 못 그려 대신 이것을 쓴다.
 * 두 배로 그려 인쇄에도 흐리지 않게 한다.
 */
function svgToPng(svg: string, width: number, height: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * 2;
      canvas.height = height * 2;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('canvas 를 못 열었다'));
        return;
      }
      context.scale(2, 2);
      context.drawImage(image, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('PNG 를 못 만들었다'));
          return;
        }
        blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer)), reject);
      }, 'image/png');
    };
    image.onerror = () => reject(new Error('SVG 를 못 읽었다'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

/** 보고서 그래프를 워드 그림으로. PDF 와 같은 그림이다. 못 그린 그래프는 빼고 나머지를 싣는다. */
export async function wordCharts(report: ReportDocument): Promise<Paragraph[]> {
  const paragraphs: Paragraph[] = [];
  for (const chart of report.charts ?? []) {
    const svg = chartSvg(chart, { standalone: true });
    const size = svg.match(/width="(\d+)" height="(\d+)"/);
    const width = Number(size?.[1] ?? 0);
    const height = Number(size?.[2] ?? 0);
    if (!width || !height) continue;
    let png: Uint8Array;
    try {
      // eslint-disable-next-line no-await-in-loop
      png = await svgToPng(svg, width, height);
    } catch {
      continue;
    }
    const shown = { width: WORD_CHART_WIDTH, height: Math.round((height * WORD_CHART_WIDTH) / width) };
    paragraphs.push(
      // 그림 위 한 줄은 그 그림의 결론(○), 그 아래 〈그림 n〉 제목 (단위).
      ...(chart.message ? [new Paragraph(`○ ${chart.message}`)] : []),
      new Paragraph({ text: figureCaption(chart, (report.charts ?? []).indexOf(chart) + 1) }),
      new Paragraph({
        children: [new ImageRun({
          // 글자로 넘기면 docx 가 base64 로 읽다가 한글에서 깨진다(Edge 실측, 2026-10-23) — UTF-8 바이트로 넘긴다.
          type: 'svg',
          data: new TextEncoder().encode(svg),
          fallback: { type: 'png', data: png },
          transformation: shown,
          altText: { name: chart.title, description: chart.title, title: chart.title },
        })],
      }),
    );
  }
  return paragraphs;
}

/**
 * 공문서 골격의 워드 — PDF(`reportHtml` 의 officialBody)와 같은 순서·기호.
 * 제목 → (기준일) · 작성 부서 → □ 요약 → □ 현황 → □ 확인 필요 사항 → □ 향후 조치(안) → 〈별첨〉 → ※.
 */
function officialWord(report: ReportDocument, charts: Paragraph[], fallback?: TableData): (Paragraph | Table)[] {
  if (report.genre === 'notice') return noticeWord(report, charts);
  let tables = 0;
  const heading = (text: string) => new Paragraph({ text, heading: HeadingLevel.HEADING_1 });
  // 줄 바로 아래 * 줄은 갈래 숫자를 센 칸(8차 검토) — PDF 와 같은 자리.
  const items = (list: string[]) => notedItems(list, report)
    .map((item) => new Paragraph(item.startsWith('* ') ? item : `○ ${item}`));
  const section = (title: string, body: (Paragraph | Table)[]) => (body.length ? [heading(`□ ${title}`), ...body] : []);
  const { points } = report;
  // 〈표 1〉 아래 * 줄은 경과 외 미실시, 〈표 2〉 위 ○ 줄은 쏠림(5차 검토) — PDF 와 같은 자리.
  const status: (Paragraph | Table)[] = report.statusTable
    ? [new Paragraph(`〈표 ${++tables}〉 ${STATUS_CAPTION}`), wordTable(report.statusTable),
      ...(report.statusNote ? [new Paragraph(`* ${report.statusNote}`)] : [])] : [];
  const exceptions: (Paragraph | Table)[] = report.exceptions
    ? [...items(report.exceptions.points ?? []), new Paragraph(exceptionCaption(report.exceptions, ++tables)),
      wordTable(report.exceptions.table)] : [];
  // 목록 표(부서별 현황은 그림 앞, 상위 명단은 그림 뒤). PDF와 같은 차례다.
  const listed = (role: 'groups' | 'top'): (Paragraph | Table)[] => tablesAt(report, role)
    .flatMap((one) => [new Paragraph(`〈표 ${++tables}〉 ${one.caption}`), wordTable(one.table)]);
  const groups = listed('groups');
  const top = listed('top');
  const table = report.table ?? fallback;
  const meta = reportMeta(report);
  // 공지는 □ 안내 사항, □ 요청 사항(PDF와 같다).
  const titles = sectionTitles(report);
  return [
    new Paragraph({ text: report.title, heading: HeadingLevel.TITLE }),
    ...(meta ? [new Paragraph(meta)] : []),
    ...section(titles.summary, points ? items(points.summary) : [new Paragraph(report.summary)]),
    // 경과 표는 사실 자료다 — □ 현황에(PDF 와 같다).
    ...section('현황', [...status, ...exceptions, ...groups, ...charts, ...top]),
    ...section(titles.checks, points ? items(points.checks) : []),
    ...section(titles.actions, points ? items(points.actions) : []),
    ...(report.detailRows === undefined && table?.rows.length ? [heading('〈별첨〉 전체 목록'), wordTable(table)] : []),
    ...reportNotes(report).map((line) => new Paragraph(line)),
  ];
}

/** 공지의 워드: PDF(noticeBody)와 같은 게시물 꼴. 제목 → 수신 → 번호 항목 → 〈참고〉 표·그림 → 게시일, 발신 부서(오른쪽). */
function noticeWord(report: ReportDocument, charts: Paragraph[]): (Paragraph | Table)[] {
  return [
    new Paragraph({ text: report.title, heading: HeadingLevel.TITLE }),
    new Paragraph(NOTICE_TO),
    ...noticeItems(report).map((item) => new Paragraph(item)),
    ...noticeTables(report).flatMap((one) => [new Paragraph(one.caption), wordTable(one.table)]),
    ...charts,
    ...noticeSign(report).map((line) => new Paragraph({ text: line, alignment: AlignmentType.RIGHT })),
  ];
}

export async function toWord(
  table: TableData | undefined,
  answer: string,
  question: string,
  report?: ReportDocument,
): Promise<void> {
  const full = report?.table ?? table;
  const note = full ? truncationNote(full) : null;
  const { table: detail, note: trimmed } = documentTable(report, full);
  const charts = report ? await wordCharts(report) : [];
  const figures = report ? reportFigures(report.kpis) : [];
  const children: (Paragraph | Table)[] = report?.basis ? officialWord(report, charts, table) : report ? [
    new Paragraph({ text: report.headline ?? report.title, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
    ...(report.period ? [new Paragraph({ text: `대상 기간  ${report.period}`, alignment: AlignmentType.CENTER })] : []),
    ...(report.audience ? [new Paragraph({ text: `보고 대상  ${report.audience}`, alignment: AlignmentType.CENTER })] : []),
    ...(figures.length ? [new Table({
      rows: [
        new TableRow({
          children: figures.map((kpi) => new TableCell({
            children: [
              new Paragraph({ text: kpi.label, alignment: AlignmentType.CENTER }),
              new Paragraph({ text: kpi.value, alignment: AlignmentType.CENTER }),
            ],
          })),
        }),
      ],
    })] : []),
    new Paragraph({ text: report.summaryHeading ?? '현황 요약', heading: HeadingLevel.HEADING_1 }),
    new Paragraph(report.summary),
    ...charts,
    ...(detail ? [
      new Paragraph({ text: report.detailHeading ?? '상세 내역', heading: HeadingLevel.HEADING_1 }),
      wordTable(detail),
    ] : []),
    ...(trimmed ? [new Paragraph(trimmed)] : []),
    ...(note ? [new Paragraph(note)] : []),
    new Paragraph({ text: '산출 기준', heading: HeadingLevel.HEADING_2 }),
    new Paragraph(report.methodology),
    ...(report.sources.length ? [new Paragraph(`조회 화면: ${report.sources.join(', ')}`)] : []),
  ] : [
    new Paragraph({ text: question, heading: HeadingLevel.HEADING_1 }),
    new Paragraph(answer),
    ...(note ? [new Paragraph(note)] : []),
    ...(detail ? [wordTable(detail)] : []),
  ];
  save(await Packer.toBlob(new Document({ sections: [{ children }] })), fileName(question, 'docx'));
}

export function toPdf(
  table: TableData | undefined,
  answer: string,
  question: string,
  report?: ReportDocument,
): void {
  // 틀은 런타임이 만든다(`reportHtml.ts`) — 시험할 수 있고, 예전의 영문 머리말·색 띠 카드를 걷어냈다.
  const html = reportHtml(report, { answer, question, table });

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '100%';
  document.body.appendChild(frame);

  const document_ = frame.contentDocument;
  if (!document_) {
    frame.remove();
    return;
  }
  document_.open();
  document_.write(html);
  document_.close();

  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  window.setTimeout(() => frame.remove(), 60000);
}

const SLIDE_FONT = 'Malgun Gothic';
const SLIDE_INK = '0B0B0B';
const SLIDE_MUTED = '52514E';
const SLIDE_LINE = 'E1E0D9';
/** 보고용 결론 줄(거버닝 메시지)의 색: 짙은 남색 하나. */
const SLIDE_ACCENT = '1F3A5F';
/** 마케팅형(키노트처럼): 어두운 바탕, 흰 글자, 강조색 하나, 나머지 회색(조사 2026-10-28: huashu-design, Knaflic). */
const KEYNOTE_BG = '111111';
const KEYNOTE_INK = 'FFFFFF';
const KEYNOTE_MUTED = '9B9B9B';
const KEYNOTE_LINE = '3A3A3A';
const KEYNOTE_BAR = '5F5F5F';
const KEYNOTE_ACCENT = '2997FF';
/** 16:9 넓은 판(13.33 × 7.5 인치). 여백 0.5 인치. */
const SLIDE_WIDTH = 13.33;
const SLIDE_MARGIN = 0.5;

function hex(color: string): string {
  return color.replace('#', '').toUpperCase();
}

/**
 * 보고서를 발표자료로. 슬라이드 나누기는 런타임(`slides.ts`)이 정하고 여기선 옮기기만 한다.
 * 그래프는 그림이 아니라 PowerPoint 차트로 넣는다 — 받은 사람이 숫자를 고칠 수 있다.
 * 흰 바탕·제목 밑 강조선 없음·본문 왼쪽 정렬: AI 가 만든 발표자료의 기본 모양을 피한다(2026-10-23 조사).
 */
export async function toSlides(
  table: TableData | undefined,
  answer: string,
  question: string,
  report?: ReportDocument,
): Promise<void> {
  const document_ = report ?? {
    title: question,
    period: '',
    kpis: [],
    summary: answer,
    table,
    methodology: '',
    sources: [],
    generatedAt: new Date().toISOString(),
  };
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  const width = SLIDE_WIDTH - SLIDE_MARGIN * 2;
  // 마케팅형(키노트처럼)은 어두운 바탕에 강조색 하나, 나머지 회색. 보고용은 흰 바탕(사용자 결정, 2026-10-28).
  // 장 구성(reportSlides)과 같은 판정이다. 공지와 기준일 없는 옛 보고서는 마케팅형이 아니다.
  const keynote = keynoteDeck(document_);
  const ink = keynote ? KEYNOTE_INK : SLIDE_INK;
  const muted = keynote ? KEYNOTE_MUTED : SLIDE_MUTED;
  const line = keynote ? KEYNOTE_LINE : SLIDE_LINE;
  const heading = (slide: PptxGenJS.Slide, text: string, size = 26, height = 0.9) => slide.addText(text, {
    x: SLIDE_MARGIN, y: 0.4, w: width, h: height, fontFace: SLIDE_FONT, fontSize: size, bold: true, color: ink, valign: 'top', lang: 'ko-KR',
  });
  // 위·오른쪽·아래·왼쪽. 행 사이에 옅은 선 하나만 — 칸마다 테두리를 두르지 않는다.
  const border = (bottom: number, color: string): [
    PptxGenJS.BorderProps, PptxGenJS.BorderProps, PptxGenJS.BorderProps, PptxGenJS.BorderProps,
  ] => [{ type: 'none' }, { type: 'none' }, { type: 'solid', pt: bottom, color }, { type: 'none' }];
  const addTable = (slide: PptxGenJS.Slide, table: { columns: string[]; rows: string[][] }, y: number) => slide.addTable([
    table.columns.map((column) => ({ text: column, options: { bold: true, border: border(1.5, ink) } })),
    ...table.rows.map((row) => row.map((cell) => ({ text: cell, options: { border: border(0.75, line) } }))),
  ], {
    x: SLIDE_MARGIN, y, w: width, fontFace: SLIDE_FONT, fontSize: 12, color: ink,
  });
  const caption = (slide: PptxGenJS.Slide, text: string, y: number) => slide.addText(text, {
    x: SLIDE_MARGIN, y, w: width, h: 0.4, fontFace: SLIDE_FONT, fontSize: 14, color: muted, lang: 'ko-KR',
  });
  // 바닥(보고용): 왼쪽 아래 출처, 오른쪽 아래 쪽번호. 화면 경로 꼴의 출처는 싣지 않는다.
  const sources = (document_.sources ?? []).filter((one) => !one.startsWith('/'));
  const slides = reportSlides(document_);
  slides.forEach((one, index) => {
    const slide = pptx.addSlide();
    slide.background = { color: keynote ? KEYNOTE_BG : 'FFFFFF' };
    if (one.kind === 'title') {
      slide.addText(one.title, {
        x: SLIDE_MARGIN, y: 2.4, w: width, h: 1.6, fontFace: SLIDE_FONT, fontSize: keynote ? 40 : 36, bold: true, color: ink, valign: 'bottom', lang: 'ko-KR',
      });
      slide.addText(one.subtitle, {
        x: SLIDE_MARGIN, y: 4.1, w: width, h: 0.6, fontFace: SLIDE_FONT, fontSize: 16, color: muted, lang: 'ko-KR',
      });
      return;
    }
    if (!keynote && document_.basis) {
      if (sources.length) {
        slide.addText(`출처: ${sources.join('; ')}`, {
          x: SLIDE_MARGIN, y: 7.0, w: width * 0.8, h: 0.3, fontFace: SLIDE_FONT, fontSize: 10, color: muted, lang: 'ko-KR',
        });
      }
      slide.addText(`${index}/${slides.length - 1}`, {
        x: SLIDE_MARGIN + width * 0.8, y: 7.0, w: width * 0.2, h: 0.3, fontFace: SLIDE_FONT, fontSize: 10, color: muted, align: 'right',
      });
    }
    // 마케팅형의 한 문장 장과 큰 숫자 장.
    if (one.kind === 'statement') {
      slide.addText(one.title, {
        x: SLIDE_MARGIN, y: 1.6, w: width, h: 3.6, fontFace: SLIDE_FONT, fontSize: one.size, bold: true, color: ink, valign: 'middle', lang: 'ko-KR',
      });
      if (one.caption) caption(slide, one.caption, 5.4);
      return;
    }
    if (one.kind === 'number') {
      slide.addText(one.value, {
        x: SLIDE_MARGIN, y: 1.4, w: width, h: 3.4, fontFace: SLIDE_FONT, fontSize: one.size, bold: true, color: KEYNOTE_ACCENT, valign: 'bottom',
      });
      slide.addText(one.title, {
        x: SLIDE_MARGIN, y: 5.0, w: width, h: 0.8, fontFace: SLIDE_FONT, fontSize: 28, color: ink, lang: 'ko-KR',
      });
      return;
    }
    // 보고용(한국형): 번호 붙은 주제 제목 아래 결론 한 줄(거버닝 메시지). 결론이 주제와 같으면(조치 장) 주제만.
    const topic = 'topic' in one ? one.topic : undefined;
    const sized = 'size' in one ? one.size : undefined;
    if (topic && !keynote) {
      slide.addText(topic, {
        x: SLIDE_MARGIN, y: 0.35, w: width, h: 0.55, fontFace: SLIDE_FONT, fontSize: 22, bold: true, color: ink, valign: 'top', lang: 'ko-KR',
      });
      // 결론 줄이 주제 제목과 같은 말이면(조치 장) 되풀이하지 않는다.
      if (!topic.endsWith(one.title)) {
        slide.addText(one.title, {
          x: SLIDE_MARGIN, y: 0.95, w: width, h: 0.75, fontFace: SLIDE_FONT, fontSize: sized ?? 18, color: SLIDE_ACCENT, valign: 'top', lang: 'ko-KR',
        });
      }
    } else {
      // 마케팅형 그림 장 제목은 길이로 고른 크기, 그 밖(다음 할 일, 참고)은 32pt. 다음 할 일의 size는 항목 크기다.
      // 마케팅형 그림 장 제목은 두 줄까지 고른 크기라 상자를 두 줄 높이로 둔다(독립 검토: 32pt 두 줄이 캡션을 덮었다).
      // 마케팅형 참고 표 장도 제목(「참고: 표 이름」)이 두 줄이 될 수 있어 같은 높이를 둔다(독립 재검토).
      const tall = keynote && (one.kind === 'chart' || one.kind === 'table');
      heading(slide, keynote && topic ? `${topic}: ${one.title}` : one.title, keynote ? (one.kind === 'chart' ? sized ?? 28 : 32) : 26,
        tall ? 1.25 : 0.9);
    }
    // 본문 시작: 보고용 주제·결론 두 줄 아래 1.8, 마케팅형 그림·참고 표 장은 두 줄 제목 아래 2.05, 그 밖 1.5(예전 자리).
    const korean = !!topic && !keynote;
    const top = korean ? 1.8 : keynote && (one.kind === 'chart' || one.kind === 'table') ? 2.05 : 1.5;
    if (one.kind === 'points') {
      let y = top;
      const figures = (one.figures ?? []).slice(0, 4);
      if (figures.length) {
        figures.forEach((figure, index) => {
          slide.addText([
            {
              text: figure.value,
              options: {
                fontSize: 32, bold: true, color: ink, breakLine: true,
              },
            },
            { text: figure.label, options: { fontSize: 14, color: muted } },
          ], {
            x: SLIDE_MARGIN + index * (width / figures.length),
            y,
            w: width / figures.length - 0.2,
            h: 1.2,
            fontFace: SLIDE_FONT,
            valign: 'top',
          });
        });
        y += 1.4;
      }
      if (one.items.length) {
        // 마케팅형의 다음 할 일은 크게(셋까지), 보고용은 ○ 개조식.
        const size = keynote ? one.size ?? 24 : 18;
        const height = Math.min((keynote ? 0.9 : 0.5) * one.items.length + 0.2, keynote ? 4.5 : 3);
        slide.addText(one.items.map((item, at) => ({
          text: keynote ? `${at + 1}  ${item}` : item.startsWith('* ') ? item : `○ ${item}`, options: { breakLine: true },
        })), {
          x: SLIDE_MARGIN,
          y,
          w: width,
          h: height,
          fontFace: SLIDE_FONT,
          fontSize: size,
          color: ink,
          valign: 'top',
          paraSpaceAfter: keynote ? 14 : 6,
          lang: 'ko-KR',
        });
        y += height + 0.2;
      }
      if (one.table) {
        caption(slide, one.table.title, y);
        addTable(slide, one.table, y + 0.45);
      }
      return;
    }
    if (one.kind === 'chart') {
      const { chart } = one;
      // 보고용은 결론 줄이 두 줄일 수 있어 캡션을 본문 시작 자리에 둔다(독립 검토: 두 줄 결론이 캡션과 겹쳤다).
      if (one.caption) caption(slide, one.caption, korean ? top : top - 0.35);
      // 반 일(45.5일)이 있으면 소수 한 자리까지 찍는다. 장 제목은 45.5인데 막대에는 46이 찍혔다(독립 검토).
      const fractional = chart.series.some((series) => series.values.some((value) => value !== null && !Number.isInteger(value)));
      // 바닥 출처 줄(보고용) 위까지.
      const chartTop = korean ? top + 0.45 : top + 0.1;
      const common = {
        x: SLIDE_MARGIN,
        y: chartTop,
        w: width,
        h: 6.9 - chartTop,
        showValue: true,
        dataLabelFormatCode: fractional ? '#,##0.0' : '#,##0',
        dataLabelColor: muted,
        dataLabelFontFace: SLIDE_FONT,
        dataLabelFontSize: keynote ? 16 : 12,
        catAxisLabelFontFace: SLIDE_FONT,
        catAxisLabelFontSize: keynote ? 16 : 13,
        catAxisLabelColor: ink,
        valAxisHidden: true,
        valGridLine: { style: 'none' as const },
        catGridLine: { style: 'none' as const },
        legendPos: 't' as const,
        legendFontFace: SLIDE_FONT,
        legendFontSize: 13,
        legendColor: ink,
      };
      // 마케팅형: 막대는 회색, 그림의 결론을 지는 막대 하나만 강조색, 범례 없음(계열 하나에 값마다 색이 들어간다).
      if (keynote && one.emphasis !== undefined && chart.series.length === 1) {
        const values = (chart.series[0]?.values ?? []).map((value) => value ?? 0);
        const order = chart.categories.map((_, at) => at).reverse();
        slide.addChart(pptx.ChartType.bar, [{
          name: chart.series[0]?.name ?? '', labels: order.map((at) => chart.categories[at] ?? ''), values: order.map((at) => values[at] ?? 0),
        }], {
          ...common, barDir: 'bar', barGapWidthPct: 50, dataLabelPosition: 'outEnd', dataLabelColor: ink, showLegend: false,
          chartColors: order.map((at) => (at === one.emphasis ? KEYNOTE_ACCENT : KEYNOTE_BAR)),
        });
        return;
      }
      if (chart.kind === 'share') {
        // 100% 누적 막대 하나 — 칸마다 계열 하나, 값은 비율(%)로 넣는다.
        const values = (chart.series[0]?.values ?? []).map((value) => (value === null ? 0 : value));
        const total = values.reduce((sum, value) => sum + value, 0) || 1;
        slide.addChart(pptx.ChartType.bar, chart.categories.map((category, index) => ({
          name: category, labels: [''], values: [Math.round(((values[index] ?? 0) / total) * 1000) / 10],
        })), {
          ...common,
          h: 2.2,
          barDir: 'bar',
          barGrouping: 'percentStacked',
          showLegend: true,
          legendPos: 'b',
          dataLabelPosition: 'ctr',
          dataLabelFormatCode: '0.0"%"',
          dataLabelColor: 'FFFFFF',
          catAxisHidden: true,
          // 칸을 계열처럼 보고 같은 색 규칙 — 주의 칸만 주황.
          chartColors: chart.categories.map((category) => hex(seriesColor({
            ...chart, series: chart.categories.map((name) => ({ name, values: [] })),
          }, category))),
        });
        return;
      }
      const data = chart.series.map((series) => ({
        name: series.name, labels: chart.categories, values: series.values as number[],
      }));
      const colored = { ...common, chartColors: chart.series.map((series) => hex(seriesColor(chart, series.name))), showLegend: chart.series.length > 1 };
      if (chart.kind === 'line') {
        slide.addChart(pptx.ChartType.line, data, {
          ...colored, lineSize: 2, lineDataSymbol: 'circle', lineDataSymbolSize: 8, displayBlanksAs: 'gap', dataLabelPosition: 't',
        });
      } else {
        // 가로 막대. PowerPoint 는 첫 범주를 맨 아래에 둔다 — 뒤집어 넣어 첫 범주가 위에 오게 한다.
        const flipped = data.map((series) => ({
          ...series, labels: [...series.labels].reverse(), values: [...series.values].reverse(),
        }));
        // 누적(경과·예정)이면 쌓아 그린다 — 누적 막대의 숫자는 칸 가운데에만 둘 수 있다(outEnd 는 파일을 깨뜨린다).
        slide.addChart(pptx.ChartType.bar, flipped, chart.kind === 'stack'
          ? {
            ...colored, showLegend: true, barDir: 'bar', barGrouping: 'stacked', barGapWidthPct: 60, dataLabelPosition: 'ctr', dataLabelColor: 'FFFFFF',
          }
          : {
            ...colored, barDir: 'bar', barGapWidthPct: 60, dataLabelPosition: 'outEnd',
          });
      }
      return;
    }
    addTable(slide, one, top);
  });
  const blob = await pptx.write({ outputType: 'blob' }) as Blob;
  save(await plainCategories(blob), fileName(question, 'pptx'));
}

const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * 차트 범주를 일반 꼴로 바꾼다(`plainCategoryXml`). pptxgenjs는 막대 범주를 늘 다단계로 써서, 그것을 못 읽는 뷰어에서 범주 이름 대신
 * 번호만 보였다(사용자, 2026-10-28). 바꿀 차트가 없으면 받은 파일 그대로다.
 */
async function plainCategories(blob: Blob): Promise<Blob> {
  const zip = await JSZip.loadAsync(await blob.arrayBuffer());
  const charts = Object.keys(zip.files).filter((name) => /^ppt\/charts\/chart\d+\.xml$/.test(name));
  let changed = false;
  await Promise.all(charts.map(async (name) => {
    const xml = await zip.file(name)?.async('string');
    if (!xml) return;
    const fixed = plainCategoryXml(xml);
    if (fixed === xml) return;
    zip.file(name, fixed);
    changed = true;
  }));
  if (!changed) return blob;
  const bytes = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
  return new Blob([bytes], { type: PPTX_TYPE });
}
